import { useRef, useState } from "react";
import { useError } from "../contexts/ErrorContext";
import { useLoading } from "../contexts/LoadingContext";
import { checkLiveTrackForUpdatesAPI } from "../services/liveTrakAPI";
import { getAGMsAndSubchainsAPI } from "../services/siteMapAPI";
import { nearestNeighbor, sortSubchainsByOrderNum } from "../utils/locationUtils";
import { adaptCoordinates } from "../utils/googleUtils";

/**
 * Used to poll the server for data, used during the lifecycle of Live Track, Live track Editing, Site Map, and Site Map (w/ pig)
 * 
 * @returns {Function} mergeLiveTrackWithRouteArr
 * @returns {Function} fetchRouteArr
 * @returns {Function} checkLiveTrackForUpdates
 * @returns {Object[]} mergedLTandRoute
 * @returns {React.RefObject<HTMLDivElement>} routeArrRef
 * @returns {React.RefObject<HTMLDivElement>} agmArrRef
 * @returns {Object[]} subchains
 * @returns {Object[]} polyLineArr
 * @returns {Function} findAndStoreLastUpdateValue
 * @returns {Object[]} agms
 * @returns {Boolean} noData
 * 
 * @category Hooks
 */
const useLiveSiteMap = () => {
    const { showError } = useError();
    const { showLoading, hideLoading } = useLoading();

    const lastUpdateValue = useRef(null);
    const routeArrRef = useRef([]);
    const agmArrRef = useRef([]);
    const consecutiveFailuresRef = useRef(0); //show error message if 3 network errors in a row (don't show just for one-off)

    const [mergedLTandRoute, setMergedLTandRoute] = useState([]);
    const [agms, setAgms] = useState([]);
    const [subchains, setSubchains] = useState([]);
    const [polyLineArr, setPolyLineArr] = useState([]);
    const [isFetching, setIsFetching] = useState(false);

    const [noData, setNoData] = useState(false); //eslint-disable-line

    const clearStates = () => { //eslint-disable-line
        setMergedLTandRoute([]);
        routeArrRef.current = [];
        setAgms([]);
        agmArrRef.current = [];
        setSubchains([]);
    }

    const mergeLiveTrackWithRouteArr = (rawLiveTrackTable) => {
        try {
            if (rawLiveTrackTable.length > 0) {
                const liveTrackMap = new Map();
                rawLiveTrackTable.forEach(item => {
                    if (item.AGMID) { // && item.PassTime
                        liveTrackMap.set(item.AGMID, {
                            PassTime: item.PassTime,
                            Speed: item.Speed,
                            Approved: item.Approved
                        });
                    }
                });

                let updatedRouteArr;


                // This is likely the initial merge
                updatedRouteArr = routeArrRef.current.map(item => {
                    if (item.AGMID && liveTrackMap.has(item.AGMID)) {
                        const trackInfo = liveTrackMap.get(item.AGMID);
                        return { ...item, PassTime: trackInfo.PassTime, Speed: trackInfo.Speed, Approved: trackInfo.Approved };
                    }
                    return { ...item };
                });

                let updatedAgmArr = agmArrRef.current.map(item => {
                    if (item.AGMID && liveTrackMap.has(item.AGMID)) {
                        const trackInfo = liveTrackMap.get(item.AGMID);
                        return { ...item, PassTime: trackInfo.PassTime, Speed: trackInfo.Speed, Approved: trackInfo.Approved };
                    }
                    return { ...item };
                });
                
                setAgms(updatedAgmArr);
                setMergedLTandRoute(updatedRouteArr);
            }
        } catch (error) {
            showError(`Error Merging Route with Live Track Data - ${error.message}`);
        }
    }

    const checkLiveTrackForUpdates = async (runID, lastTime) => {
        //This is a call to the server to check if live track has any upates
        //send the last updated time value from the frontend 
        //server returns all updates that have been made since then
        //anything not returned has not changed since initial state set
        if (!lastUpdateValue.current) {
            lastUpdateValue.current = new Date()
        }
        let response = await checkLiveTrackForUpdatesAPI(lastUpdateValue.current, runID);

        if (response?.errorType) {
            // Increment consecutive failure count
            consecutiveFailuresRef.current += 1;
            
            // Only show error if we've had 3+ consecutive failures
            if (consecutiveFailuresRef.current >= 3) {
                // Enhanced error messages for better debugging infrequent network issues
                let errorMsg = response.message;
                if (response.errorType === 'NETWORK_ERROR') {
                    errorMsg += ' (This can happen if the server is temporarily unavailable or your connection drops)';
                } else if (response.errorType === 'HTTP_ERROR') {
                    errorMsg += ` (Status: ${response.status})`;
                }
                showError(`Error Fetching Live Track Updates - ${errorMsg}`);
            }
        } else {
            // Success - reset failure counter
            consecutiveFailuresRef.current = 0;
            
            lastUpdateValue.current = response.data?.lastUpdateTime;
            //have to merge to update merged table with new pass times/speed etc
            if(response.data) mergeLiveTrackWithRouteArr(response.data.liveTrackUpdates);
        }
    }

    const fetchRouteArr = async (jobID) => {
        showLoading('Fetching Routes For Map...');
        routeArrRef.current = [];
        setIsFetching(true);

        try {
            const response = await getAGMsAndSubchainsAPI(jobID);
            
            if (response?.errorType) {
                //error handling here
                setIsFetching(false);
                showError(`Error Getting Jobs - ${response.message}`);
                // clearStates();
            } else {
                setIsFetching(false);
                //calculate the routeArr for PolyLine on map
                //add a agm or subchain boolean for easier sort/find
                let respAgms = [...response.data.agms];
                respAgms.forEach(agm => agm.agm = true);
                let respSc = [...response.data.subchains];
                respSc.forEach(sc => sc.subchain = true);
                // let combinedArrays = [...response.data.agms, ...response.data.subchains];

                //should be real chain by this point
                //validation check needed
                respAgms.sort((a, b) => a.Real_Chain - b.Real_Chain);

                let route = [];
                for (let i = 0; i < respAgms.length - 1; i++) {
                    const currAgm = respAgms[i];
                    const nextAgm = respAgms[i + 1];

                    const subchainsBetweenAgms = [
                        currAgm,
                        ...response.data.subchains.filter(sc => sc.US_Site === currAgm.AGMID && sc.DS_Site === nextAgm.AGMID),
                        nextAgm
                    ];

                    let seg = sortSubchainsByOrderNum(subchainsBetweenAgms) || nearestNeighbor(subchainsBetweenAgms);

                    if (i === 0) {
                        // first segment: take it all
                        route.push(...seg);
                    } else {
                        // subsequent segments: skip the first (it's already in route)
                        route.push(...seg.slice(1));
                    }
                }
                setAgms(response.data.agms);
                agmArrRef.current = response.data.agms;
                
                setSubchains(response.data.subchains);
                // setSubchains(route);
                route = route.flat();

                let adaptRouteKeysForGoogleAPI = adaptCoordinates(route);
                setPolyLineArr(adaptRouteKeysForGoogleAPI);
                routeArrRef.current = adaptRouteKeysForGoogleAPI

            }
        } catch (error) {
            setIsFetching(false);
            showError(`Error Getting Jobs - ${error.message}`)
        } finally {
            setIsFetching(false);
            hideLoading();
        }
    };

    const findAndStoreLastUpdateValue = (rawLiveTrackTable) => {
        let lastUpdateTime = null;
        rawLiveTrackTable.forEach(row => {
            if (row.UpdatedAt !== null && lastUpdateTime === null) {
                lastUpdateTime = row.UpdatedAt;
            } else if (row.UpdatedAt !== null && row.UpdatedAt > lastUpdateTime) {
                lastUpdateTime = row.UpdatedAt;
            }
        });
        lastUpdateValue.current = lastUpdateTime;
    }

    return {
        mergeLiveTrackWithRouteArr,
        fetchRouteArr,
        checkLiveTrackForUpdates,
        mergedLTandRoute,
        routeArrRef,
        agmArrRef,
        subchains,
        polyLineArr,
        findAndStoreLastUpdateValue,
        agms,
        noData,
        isFetching
    }
}

export default useLiveSiteMap;